import type { SpeechProviderWithExtraOptions } from '@xsai-ext/shared-providers'

import type { VoiceInfo } from '../providers'

import { useLocalStorage } from '@vueuse/core'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { toXml } from 'xast-util-to-xml'
import { x } from 'xastscript'

import { generateSpeech as elevenlabsGenerateSpeech, resolveBaseUrl as resolveElevenlabsBaseUrl } from '../../services/elevenlabs'
import { generateSpeech as fishAudioGenerateSpeech } from '../../services/fish-audio'
import { generateSpeech as minimaxGenerateSpeech, generateSpeechT2A as minimaxGenerateSpeechT2A } from '../../services/minimax'
import { cleanTextForTTS } from '../../utils/tts'
import { useProvidersStore } from '../providers'

const MINIMAX_PROD_BASE_URL = 'https://api.minimax.io'
const MINIMAX_CN_BASE_URL = 'https://api.minimaxi.com'
const MINIMAX_DEV_PROXY_BASE_URL = '/api/minimax'
const MINIMAX_CN_DEV_PROXY_BASE_URL = '/api/minimax-cn'

function resolveMinimaxBaseUrl(configured?: string, isCN = false) {
  const trimmed = configured?.trim()
  const officialUrl = isCN ? MINIMAX_CN_BASE_URL : MINIMAX_PROD_BASE_URL
  const devProxy = isCN ? MINIMAX_CN_DEV_PROXY_BASE_URL : MINIMAX_DEV_PROXY_BASE_URL

  if (import.meta.env.DEV) {
    if (!trimmed || trimmed === officialUrl) {
      return devProxy
    }
    return trimmed
  }

  return trimmed || officialUrl
}

const FISH_AUDIO_OFFICIAL_BASE_URL = 'https://api.fish.audio'
/** Fish Audio 不支持浏览器跨域（preflight 无 ACAO 头），dev 和生产均走同源代理 */
const FISH_AUDIO_PROXY_BASE_URL = '/api/fish-audio'

/**
 * 归一 Fish Audio 请求地址：
 * - dev/生产：空 或 官方地址 → 同源代理（dev 由 Vite proxy，生产由 nginx 转发）
 * - 填了自建代理地址则原样用（用户自己的中转服务器）
 */
function resolveFishAudioBaseUrl(configured?: string): string {
  const trimmed = configured?.trim().replace(/\/+$/, '')
  if (!trimmed || trimmed === FISH_AUDIO_OFFICIAL_BASE_URL) {
    return FISH_AUDIO_PROXY_BASE_URL
  }
  return trimmed
}

export const useSpeechStore = defineStore('speech', () => {
  const providersStore = useProvidersStore()
  const { allAudioSpeechProvidersMetadata } = storeToRefs(providersStore)

  // State
  const activeSpeechProvider = useLocalStorage('settings/speech/active-provider', '')
  const activeSpeechModel = useLocalStorage('settings/speech/active-model', 'speech-2.6-hd')
  const activeSpeechVoiceId = useLocalStorage<string>('settings/speech/voice', '')
  const activeSpeechVoice = ref<VoiceInfo>()

  const pitch = useLocalStorage('settings/speech/pitch', 0)
  const rate = useLocalStorage('settings/speech/rate', 1)
  const ssmlEnabled = useLocalStorage('settings/speech/ssml-enabled', false)
  const isLoadingSpeechProviderVoices = ref(false)
  const speechProviderError = ref<string | null>(null)
  const availableVoices = ref<Record<string, VoiceInfo[]>>({})
  const selectedLanguage = useLocalStorage('settings/speech/language', 'en-US')
  const modelSearchQuery = ref('')

  // Computed properties
  const availableSpeechProvidersMetadata = computed(() => allAudioSpeechProvidersMetadata.value)

  // Computed properties
  const supportsModelListing = computed(() => {
    return providersStore.getProviderMetadata(activeSpeechProvider.value)?.capabilities.listModels !== undefined
  })

  const providerModels = computed(() => {
    return providersStore.getModelsForProvider(activeSpeechProvider.value)
  })

  const isLoadingActiveProviderModels = computed(() => {
    return providersStore.isLoadingModels[activeSpeechProvider.value] || false
  })

  const activeProviderModelError = computed(() => {
    return providersStore.modelLoadError[activeSpeechProvider.value] || null
  })

  const filteredModels = computed(() => {
    if (!modelSearchQuery.value.trim()) {
      return providerModels.value
    }

    const query = modelSearchQuery.value.toLowerCase().trim()
    return providerModels.value.filter(model =>
      model.name.toLowerCase().includes(query)
      || model.id.toLowerCase().includes(query)
      || (model.description && model.description.toLowerCase().includes(query)),
    )
  })

  const supportsSSML = computed(() => {
    // Currently only ElevenLabs and some other providers support SSML
    // only part voices are support SSML in cosyvoice-v2 which is provided by alibaba
    if (activeSpeechProvider.value === 'alibaba-cloud-model-studio' && activeSpeechModel.value === 'cosyvoice-v2') {
      return true
    }
    return ['elevenlabs', 'microsoft-speech', 'azure-speech', 'google', 'volcengine'].includes(activeSpeechProvider.value)
  })

  async function loadVoicesForProvider(provider: string) {
    if (!provider) {
      return []
    }

    if (!providersStore.providerMetadata[provider]) {
      availableVoices.value[provider] = []
      return []
    }

    isLoadingSpeechProviderVoices.value = true
    speechProviderError.value = null

    try {
      const voices = await providersStore.getProviderMetadata(provider).capabilities.listVoices?.(providersStore.getProviderConfig(provider)) || []
      availableVoices.value[provider] = voices
      return voices
    }
    catch (error) {
      console.error(`Error fetching voices for ${provider}:`, error)
      speechProviderError.value = error instanceof Error ? error.message : 'Unknown error'
      return []
    }
    finally {
      isLoadingSpeechProviderVoices.value = false
    }
  }

  // Get voices for a specific provider
  function getVoicesForProvider(provider: string) {
    return availableVoices.value[provider] || []
  }

  // Watch for provider changes and load voices
  watch(activeSpeechProvider, async (newProvider) => {
    if (newProvider) {
      await loadVoicesForProvider(newProvider)
      // Don't reset voice settings when changing providers to allow for persistence
    }
  })

  // 初始化：加载当前 provider 的声音列表
  loadVoicesForProvider(activeSpeechProvider.value).then(() => {
    if (activeSpeechVoiceId.value) {
      activeSpeechVoice.value = availableVoices.value[activeSpeechProvider.value]?.find(voice => voice.id === activeSpeechVoiceId.value)
    }
  })

  watch(activeSpeechVoiceId, (voiceId) => {
    if (voiceId) {
      activeSpeechVoice.value = availableVoices.value[activeSpeechProvider.value]?.find(voice => voice.id === voiceId)
    }
  }, {
    immediate: true,
  })

  watch(availableVoices, (voices) => {
    if (activeSpeechVoiceId.value) {
      activeSpeechVoice.value = voices[activeSpeechProvider.value]?.find(voice => voice.id === activeSpeechVoiceId.value)
    }
  }, {
    immediate: true,
  })

  /**
   * Generate speech using the specified provider and settings
   *
   * @param provider The speech provider instance
   * @param model The model to use
   * @param input The text input to convert to speech
   * @param voice The voice ID to use
   * @param providerConfig Additional provider configuration
   * @returns ArrayBuffer containing the audio data
   */
  async function speech(
    provider: SpeechProviderWithExtraOptions<string, any>,
    model: string,
    input: string,
    voice: string,
    providerConfig: Record<string, any> = {},
  ): Promise<ArrayBuffer> {
    const { generateSpeech } = await import('@xsai/generate-speech')
    const response = await generateSpeech({
      ...provider.speech(model, {
        ...providerConfig,
      }),
      input,
      voice,
    })

    return response
  }

  /**
   * Generate speech with character-specific voice (MiniMax)
   *
   * @param text Text to convert to speech
   * @param contactId Optional contact ID to use character-specific voice
   * @returns ArrayBuffer containing the audio data
   */
  async function generateSpeechForContact(
    text: string,
    contactId?: string,
  ): Promise<ArrayBuffer> {
    const cleanedText = cleanTextForTTS(text)

    if (!cleanedText) {
      throw new Error('文本内容为空')
    }

    const config = providersStore.getProviderConfig(activeSpeechProvider.value)
    if (!config || !config.apiKey) {
      throw new Error('语音服务商未配置，请先在设置中配置 API Key')
    }

    let voiceId = activeSpeechVoiceId.value

    if (contactId) {
      const { useContactsStore } = await import('../contacts')
      const contactsStore = useContactsStore()
      const contact = contactsStore.getContact(contactId)

      if (contact?.metadata?.voiceId) {
        voiceId = contact.metadata.voiceId
      }
    }

    if (!voiceId) {
      throw new Error('未配置声音，请在声音库中上传并选择声音')
    }

    const { useVoiceLibraryStore } = await import('../voice-library')
    const voiceLibraryStore = useVoiceLibraryStore()
    const voice = voiceLibraryStore.getVoice(voiceId)

    if (!voice) {
      throw new Error(`声音 ${voiceId} 不存在于声音库中`)
    }

    // 优先使用声音自身的 provider 标记，回退到全局 activeSpeechProvider（兼容旧数据）
    const effectiveProvider = (voice.metadata?.provider as string | undefined) || activeSpeechProvider.value
    const effectiveConfig = effectiveProvider !== activeSpeechProvider.value
      ? (providersStore.getProviderConfig(effectiveProvider) || config)
      : config
    const apiKey = (effectiveConfig.apiKey as string | undefined) || (config.apiKey as string)

    // ElevenLabs 路由
    if (effectiveProvider === 'elevenlabs') {
      if (!voice.fileId) {
        throw new Error(`声音 ${voiceId} 缺少 voice_id，请重新克隆或填写 voice_id`)
      }
      // 空 或 官方地址 → 归一到同源代理；仅自建代理地址才直连（生产不能浏览器直连官方，会被 CORS 拦）
      const baseUrl = resolveElevenlabsBaseUrl(effectiveConfig.baseUrl as string | undefined)
      // 不回退到全局 activeSpeechModel：它默认是 MiniMax 的 speech-2.x id，发给 ElevenLabs 会 invalid-model
      const modelId = (effectiveConfig.model as string | undefined) || 'eleven_multilingual_v2'

      return await elevenlabsGenerateSpeech({
        text: cleanedText,
        voiceId: voice.fileId,
        apiKey,
        baseUrl,
        modelId,
      })
    }

    // Fish Audio 路由
    if (effectiveProvider === 'fish-audio') {
      if (!voice.fileId) {
        throw new Error(`声音 ${voiceId} 缺少远程标识符，请重新上传音频`)
      }
      // Fish Audio 官方不支持跨域，dev/生产统一走同源代理（/api/fish-audio）
      const baseUrl = resolveFishAudioBaseUrl(effectiveConfig.baseUrl as string | undefined)
      const model = (effectiveConfig.model as string | undefined) || activeSpeechModel.value || 's2.1-pro'

      return await fishAudioGenerateSpeech({
        text: cleanedText,
        referenceId: voice.fileId,
        apiKey,
        baseUrl,
        model,
      })
    }

    // MiniMax 路由（国际版 + 国内版）
    const isCN = effectiveProvider === 'minimax-speech-cn'
    const baseUrl = resolveMinimaxBaseUrl(effectiveConfig.baseUrl as string | undefined, isCN)
    const model = (effectiveConfig.model as string | undefined) || activeSpeechModel.value || 'speech-2.8-hd'

    // 检查是否有持久化的 voice_id（可用 T2A V2）
    const persistentVoiceId = voice.metadata?.minimaxVoiceId as string | undefined

    if (!voice.fileId && !persistentVoiceId) {
      throw new Error(`声音 ${voiceId} 缺少远程标识符，请重新上传音频或填写音色 ID`)
    }

    if (persistentVoiceId) {
      // T2A V2 路由（持久化 voice_id）
      return await minimaxGenerateSpeechT2A({
        text: cleanedText,
        voiceId: persistentVoiceId,
        apiKey,
        baseUrl,
        model,
      })
    }

    // 旧版 voice_clone 路由 — 首次调用时克隆并持久化 voice_id，后续自动走 T2A V2
    // MiniMax voice_id 只允许字母、数字和下划线，替换掉连字符
    const newPersistentId = `meowchat_${voice.id}`.replace(/-/g, '_')
    // voice_clone 接口不支持 turbo 系列，降级为对应 hd 版本
    const cloneModel = model.replace('-turbo', '-hd')

    let audioBuffer: ArrayBuffer
    try {
      audioBuffer = await minimaxGenerateSpeech({
        text: cleanedText,
        runtimeVoiceId: newPersistentId,
        fileId: String(voice.fileId!),
        apiKey,
        baseUrl,
        model: cloneModel,
      })
    }
    catch (cloneError) {
      // voice_id 已存在于 MiniMax（上次克隆成功但持久化失败），直接走 T2A V2
      const msg = cloneError instanceof Error ? cloneError.message.toLowerCase() : ''
      if (msg.includes('duplicate') || msg.includes('already exist') || msg.includes('重复') || msg.includes('1013')) {
        const { useVoiceLibraryStore: useVLS } = await import('../voice-library')
        useVLS().updateVoice(voice.id, {
          metadata: { ...voice.metadata, minimaxVoiceId: newPersistentId },
        })
        console.info(`✅ voice_id 已存在，持久化并走 T2A V2: ${newPersistentId}`)
        return await minimaxGenerateSpeechT2A({ text: cleanedText, voiceId: newPersistentId, apiKey, baseUrl, model })
      }
      throw cloneError
    }

    // 克隆成功，持久化 voice_id 到声音库，下次直接走 T2A V2
    try {
      const { useVoiceLibraryStore } = await import('../voice-library')
      const voiceLibraryStore = useVoiceLibraryStore()
      voiceLibraryStore.updateVoice(voice.id, {
        metadata: { ...voice.metadata, minimaxVoiceId: newPersistentId },
      })
      console.info(`✅ MiniMax voice_id 已持久化: ${newPersistentId}`)
    }
    catch (e) {
      console.warn('持久化 voice_id 失败，下次仍走 voice_clone:', e)
    }

    return audioBuffer
  }

  function generateSSML(
    text: string,
    voice: VoiceInfo,
    providerConfig?: Record<string, any>,
  ): string {
    const pitch = providerConfig?.pitch
    const speed = providerConfig?.speed
    const volume = providerConfig?.volume

    const prosody = {
      pitch: pitch != null
        ? pitch > 0
          ? `+${pitch}%`
          : `-${pitch}%`
        : undefined,
      rate: speed != null
        ? speed !== 1.0
          ? `${speed}`
          : '1'
        : undefined,
      volume: volume != null
        ? volume > 0
          ? `+${volume}%`
          : `${volume}%`
        : undefined,
    }

    const ssmlXast = x('speak', { 'version': '1.0', 'xmlns': 'http://www.w3.org/2001/10/synthesis', 'xml:lang': voice.languages[0]?.code || 'en-US' }, [
      x('voice', { name: voice.id, gender: voice.gender || 'neutral' }, [
        Object.entries(prosody).filter(([_, value]) => value != null).length > 0
          ? x('prosody', {
              pitch: pitch != null ? pitch > 0 ? `+${pitch}%` : `-${pitch}%` : undefined,
              rate: speed != null ? speed !== 1.0 ? `${speed}` : '1' : undefined,
              volume: volume != null ? volume > 0 ? `+${volume}%` : `${volume}%` : undefined,
            }, [
              text,
            ])
          : text,
      ]),
    ])

    return toXml(ssmlXast)
  }

  const configured = computed(() => {
    return !!activeSpeechProvider.value && !!activeSpeechModel.value && !!activeSpeechVoiceId.value
  })

  return {
    // State
    configured,
    activeSpeechProvider,
    activeSpeechModel,
    activeSpeechVoice,
    activeSpeechVoiceId,
    pitch,
    rate,
    ssmlEnabled,
    selectedLanguage,
    isLoadingSpeechProviderVoices,
    speechProviderError,
    availableVoices,
    modelSearchQuery,

    // Computed
    availableSpeechProvidersMetadata,
    supportsSSML,
    supportsModelListing,
    providerModels,
    isLoadingActiveProviderModels,
    activeProviderModelError,
    filteredModels,

    // Actions
    speech,
    generateSpeechForContact,
    loadVoicesForProvider,
    getVoicesForProvider,
    generateSSML,
  }
})
